import React, { useEffect, useState } from 'react';
import { scrollToTemplates } from './Hero';
import './Navbar.css';
import OwnezzyLogo from './OwnezzyLogo';
import { useOwnezzyContact, whatsappLink } from '../legal/contact';
import { openSearchModal } from '../utils/searchModal';

// Top bar: logo, section links (the one in view is underlined) and global search.
// On phones the links move into a side drawer that slides in from the right and closes after a tap.
// Section links (Home, Select Website, Plan) scroll on the home page; on other pages they open
// the home page at that section (App.js scrolls to the #hash).
const LINKS = [
  { id: 'top', label: 'Home', icon: 'bi-house-door' },
  { id: 'websites', label: 'All Websites', icon: 'bi-grid-1x2', href: '/websites' },
  { id: 'plans', label: 'Plan', icon: 'bi-tags' },
  { id: 'partner', label: 'Become a partner', icon: 'bi-people', href: '/partner' },
  { id: 'guide', label: 'User Guide', icon: 'bi-book', href: '/guide' },
  { id: 'contact', label: 'Contact Us', icon: 'bi-chat-dots', href: '/contact' },
];

// The template list has a phone and a desktop version (see Hero.js); either one counts as "Select Website"
const SECTION_IDS = { top: ['top'], plans: ['plans'] };
const HELP_MESSAGE = 'Hi, I need help setting up my Ownezzy store.';

const currentPath = () => window.location.pathname.replace(/\/+$/, '');

export default function Navbar() {
  const contact = useOwnezzyContact();
  const onHome = currentPath() === '';
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(
    onHome
      ? 'top'
      : { '/websites': 'websites', '/templates': 'websites', '/partner': 'partner', '/contact': 'contact', '/guide': 'guide' }[currentPath()] || (currentPath().startsWith('/guide/') ? 'guide' : '')
  );

  // Shadow under the bar once the page is scrolled, and which section is in view
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      if (!onHome) return;
      const line = window.innerHeight * 0.35;
      let current = 'top';
      LINKS.forEach(({ id }) => {
        const el = (SECTION_IDS[id] || []).map((s) => document.getElementById(s)).find((e) => e && e.offsetParent !== null);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onHome]);

  // While the drawer is open the page behind it doesn't scroll, and Escape closes it
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hrefFor = ({ id, href }) => href || (onHome ? `#${id}` : `/#${id}`);

  const goTo = (e, link) => {
    setOpen(false);
    if (link.href || !onHome) return; // a normal page load
    e.preventDefault();
    document.getElementById(link.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const linkList = (className) => (
    <ul className={className}>
      {LINKS.map((link) => (
        <li key={link.id}>
          <a
            href={hrefFor(link)}
            className={active === link.id ? 'active' : ''}
            aria-current={active === link.id ? (link.href ? 'page' : 'location') : undefined}
            onClick={(e) => goTo(e, link)}
          >
            <i className={`bi ${link.icon}`} aria-hidden="true" />
            <span>{link.label}</span>
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <nav className={`ez-nav ${scrolled ? 'is-scrolled' : ''}`} id="site-nav">
      <div className="ez-nav-inner">
        <a className="ez-brand" href={onHome ? '#top' : '/'} onClick={(e) => goTo(e, LINKS[0])}>
          <OwnezzyLogo className="ez-brand-logo" />
        </a>

        <div className="ez-nav-menu">
          {linkList('ez-nav-links')}
        </div>

        {/* Global Search Trigger in Navbar (Desktop) */}
        <button
          type="button"
          className="ez-nav-search-trigger"
          onClick={() => openSearchModal()}
          aria-label="Search website templates"
          title="Search 100+ websites (⌘K)"
        >
          <span className="search-trigger-icon" aria-hidden="true">🔍</span>
          <span className="search-trigger-text">Search 100+ websites...</span>
          <kbd className="search-trigger-kbd">⌘K</kbd>
        </button>

        {/* Mobile Search Icon Button */}
        <button
          type="button"
          className="ez-nav-search-mobile-btn"
          aria-label="Search websites"
          title="Search websites"
          onClick={() => openSearchModal()}
        >
          <i className="bi bi-search" aria-hidden="true" />
        </button>

        <button
          type="button"
          className="ez-nav-toggle"
          aria-label="Open menu"
          aria-expanded={open}
          aria-controls="ez-drawer"
          onClick={() => setOpen(true)}
        >
          <i className="bi bi-list" />
        </button>
      </div>

      {/* Phone menu: a drawer from the right over a dimmed page; tapping outside closes it */}
      <div className={`ez-drawer-backdrop ${open ? 'is-open' : ''}`} onClick={() => setOpen(false)} aria-hidden="true" />
      <aside
        className={`ez-drawer ${open ? 'is-open' : ''}`}
        id="ez-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        inert={!open}
      >
        <div className="ez-drawer-head">
          <span className="ez-drawer-logo"><OwnezzyLogo className="ez-brand-logo" /></span>
          <button type="button" className="ez-drawer-close" aria-label="Close menu" onClick={() => setOpen(false)}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <p className="ez-drawer-tagline">Free website for your shop. Ready in minutes.</p>

        {/* Mobile Search inside Drawer */}
        <button
          type="button"
          className="ez-drawer-search-btn"
          onClick={() => {
            setOpen(false);
            openSearchModal();
          }}
        >
          <i className="bi bi-search" aria-hidden="true" />
          <span>Search 100+ website templates...</span>
        </button>

        {linkList('ez-drawer-links')}
        <div className="ez-drawer-foot">
          <a className="ez-drawer-help" href={whatsappLink(contact, HELP_MESSAGE)} target="_blank" rel="noreferrer">
            <i className="bi bi-whatsapp" /> Need help? Chat on WhatsApp
          </a>
        </div>
      </aside>
    </nav>
  );
}
